import { useMutation } from "@tanstack/react-query"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Modal from "@/globalComponents/molecules/Modal"
import TextField from "@/globalComponents/molecules/TextField"
import { ExpectedError } from "@/utilities/expectedErrors"
import { phoneValidationRule, requiredRule, validEmailRule } from "@/utilities/form"

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  receiptID: string
  method: "phone" | "email"
  defaultValue: string
}

interface SendReceiptFormSchema {
  input: string
}

function SendReceiptModal(props: Props) {
  const { t } = useTranslation(["receipt", "errors"])

  const form = useForm<SendReceiptFormSchema>({
    defaultValues: {
      input: props.defaultValue,
    },
    mode: "onBlur",
  })
  const { errors } = form.formState

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <Modal {...props} onOpenChange={handleOpenChange}>
      <Modal.Header>
        <Heading level={2} slot="title">
          {t("sendReceiptModal.header")}
        </Heading>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset disabled={isPending}>
            {errors?.root && <FieldError>{errors.root.message}</FieldError>}
            {props.method === "phone" ? (
              <Controller<SendReceiptFormSchema, "input">
                name="input"
                control={form.control}
                rules={{
                  validate: phoneValidationRule(t),
                }}
                render={({
                  field: { ref, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField {...rest} isInvalid={invalid}>
                    <Label>{t("sendReceiptModal.mobileFieldLabel")}</Label>
                    <TextField.PhoneWithCountrySelect
                      innerRef={ref}
                      defaultCountry="US"
                      placeholder="(123) 456-7890"
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            ) : (
              <Controller<SendReceiptFormSchema, "input">
                name="input"
                control={form.control}
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                  pattern: validEmailRule(
                    t("common.validEmail", { ns: "errors" })
                  ),
                }}
                render={({
                  field: { ref, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField {...rest} isInvalid={invalid}>
                    <Label>{t("sendReceiptModal.emailFieldLabel")}</Label>
                    <TextField.Input
                      innerRef={ref}
                      placeholder="alex.smith@example.com"
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            )}
            <Button type="submit" buttonType="primary" isPending={isPending}>
              {t("sendReceiptModal.submit")}
            </Button>
          </fieldset>
        </form>
      </Modal.Body>
    </Modal>
  )

  function onSubmit(data: SendReceiptFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: SendReceiptFormSchema) {
    if (props.method === "phone") {
      return api.textReceipt.POST({
        receiptID: props.receiptID,
        phone: data.input,
      })
    } else {
      return api.emailReceipt.POST({
        receiptID: props.receiptID,
        email: data.input,
      })
    }
  }

  function onSuccess() {
    handleOpenChange(false)
  }

  function onError(error: Error) {
    if (error instanceof ExpectedError) {
      form.setError("input", {
        message: t(error.translationKey, { ns: "errors" }),
      })
    } else {
      form.setError("input", { message: error.message })
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      form.reset({ input: props.defaultValue })
    }
    props.onOpenChange(isOpen)
  }
}

export default SendReceiptModal
